import { API_URL } from "../utils/urls"; // PT-REACT-URLS v1
import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { onAuthStateChanged } from "firebase/auth";
import { authentication } from "../Firebase";
import PTIdCard from "../utils/idcardRender";
import qrcode from "../utils/qrcodeGenerator";

// ID card on the profile page: apply with photo -> admin approves -> view and download.
const API_BASE = API_URL + "idcard";
const ORANGE = "#f57c00";
const NAVY = "#1b2a4a";
const MAX_PHOTO_BYTES = 3 * 1024 * 1024;

const firebaseToken = () =>
    new Promise((resolve) => {
        const finish = (user) => {
            if (!user) return resolve("");
            user.getIdToken().then(resolve).catch(() => resolve(""));
        };
        if (authentication.currentUser) return finish(authentication.currentUser);
        const unsubscribe = onAuthStateChanged(authentication, (user) => {
            unsubscribe();
            finish(user);
        });
    });

const post = async (url, fields) => {
    const form = new FormData();
    form.append("firebase_token", await firebaseToken());
    Object.entries(fields || {}).forEach(([key, value]) => {
        if (value !== undefined && value !== null) form.append(key, value);
    });
    const response = await axios.post(url, form, { validateStatus: () => true });
    const data = response.data && typeof response.data === "object" ? response.data : {};
    if (!data.success) {
        throw new Error(data.message || "Something went wrong. Please try again.");
    }
    return data;
};

const formatDate = (value) => {
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value || ""));
    return m ? `${m[3]}-${m[2]}-${m[1]}` : "";
};

const STATUS = {
    none: { label: "Not applied", color: "#6b7280" },
    pending: { label: "Waiting for approval", color: "#b45309" },
    approved: { label: "Approved", color: "#15803d" },
    expired: { label: "Expired", color: "#6b7280" },
    rejected: { label: "Rejected", color: "#b91c1c" },
    revoked: { label: "Cancelled", color: "#111827" },
};

const styles = {
    box: { border: "1px solid #eee", borderRadius: 14, padding: 20, background: "#fff", boxShadow: "0 2px 12px rgba(0,0,0,.05)" },
    heading: { color: NAVY, fontWeight: 700, marginBottom: 4 },
    badge: (color) => ({ display: "inline-block", background: color, color: "#fff", borderRadius: 999, padding: "2px 12px", fontSize: 13, fontWeight: 600 }),
    label: { fontWeight: 600, fontSize: 14, marginBottom: 4, display: "block" },
    input: { width: "100%", border: "1px solid #d1d5db", borderRadius: 8, padding: "10px 12px", fontSize: 15 },
    button: { background: ORANGE, color: "#fff", border: 0, borderRadius: 8, padding: "10px 22px", fontWeight: 600, fontSize: 15 },
    ghost: { background: "transparent", color: NAVY, border: `1px solid ${NAVY}`, borderRadius: 8, padding: "9px 18px", fontWeight: 600, fontSize: 14 },
    note: (color) => ({ background: `${color}14`, borderLeft: `4px solid ${color}`, borderRadius: 6, padding: "10px 12px", margin: "12px 0", fontSize: 14 }),
    photo: { width: 90, height: 110, objectFit: "cover", borderRadius: 8, border: `2px solid ${ORANGE}` },
    canvas: { width: "100%", maxWidth: 506, height: "auto", borderRadius: 14, boxShadow: "0 4px 18px rgba(0,0,0,.15)" },
};

const CardCanvas = ({ card, design }) => {
    const canvasRef = useRef(null);
    const [ready, setReady] = useState(false);

    useEffect(() => {
        let cancelled = false;
        setReady(false);
        if (canvasRef.current) {
            PTIdCard.draw(canvasRef.current, { ...design, ...card }, qrcode).then(() => {
                if (!cancelled) setReady(true);
            });
        }
        return () => {
            cancelled = true;
        };
    }, [card, design]);

    const download = () => {
        const name = `id-card-${String(card.card_no || "").replace(/[^A-Za-z0-9-]/g, "")}.png`;
        PTIdCard.download(canvasRef.current, name);
    };

    return (
        <div className="text-center">
            <canvas ref={canvasRef} style={styles.canvas} />
            <div className="mt-3">
                <button type="button" style={{ ...styles.button, opacity: ready ? 1 : 0.6 }} disabled={!ready} onClick={download}>
                    Download ID Card
                </button>
            </div>
        </div>
    );
};

const IdCardSection = () => {
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState("");
    const [card, setCard] = useState(null);
    const [design, setDesign] = useState(null);
    const [form, setForm] = useState({ name: "", mobile: "", address: "" });
    const [photo, setPhoto] = useState(null);
    const [photoPreview, setPhotoPreview] = useState("");
    const [showForm, setShowForm] = useState(false);
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        setLoadError("");
        try {
            const data = await post(`${API_BASE}/my`);
            setCard(data.card);
            setDesign(data.design);
            setForm({
                name: data.prefill?.name || "",
                mobile: data.prefill?.mobile || "",
                address: data.prefill?.address || "",
            });
            setShowForm(["none", "rejected"].includes(data.card?.status));
        } catch (error) {
            setLoadError(error.message);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    useEffect(() => () => photoPreview && URL.revokeObjectURL(photoPreview), [photoPreview]);

    const onPhoto = (e) => {
        const file = e.target.files && e.target.files[0];
        setMessage(null);
        if (!file) return;
        if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) {
            setMessage({ type: "error", text: "Only JPG, PNG or WEBP photos are allowed." });
            e.target.value = "";
            return;
        }
        if (file.size > MAX_PHOTO_BYTES) {
            setMessage({ type: "error", text: "Photo must be smaller than 3 MB." });
            e.target.value = "";
            return;
        }
        setPhoto(file);
        setPhotoPreview(URL.createObjectURL(file));
    };

    const submit = async (e) => {
        e.preventDefault();
        setMessage(null);
        if (card?.status === "approved" && !window.confirm("Your card will go back for approval and its QR will show 'Under review' until then. Continue?")) {
            return;
        }
        if (!photo && !card?.photo) {
            setMessage({ type: "error", text: "Please upload a passport size photo." });
            return;
        }
        setSaving(true);
        try {
            const data = await post(`${API_BASE}/apply`, { ...form, photo });
            setCard(data.card);
            setPhoto(null);
            setPhotoPreview("");
            setShowForm(false);
            setMessage({ type: "success", text: data.message });
        } catch (error) {
            setMessage({ type: "error", text: error.message });
        } finally {
            setSaving(false);
        }
    };

    const status = card?.status || "none";
    const statusInfo = STATUS[status] || STATUS.none;
    const showCard = ["approved", "expired"].includes(status) && design;

    return (
        <section className="pb-5 bg-white" id="id-card">
            <div className="container">
                <div style={styles.box}>
                    <div className="d-flex flex-wrap justify-content-between align-items-center" style={{ gap: 8 }}>
                        <div>
                            <h4 style={styles.heading}>My ID Card</h4>
                            <div style={{ color: "#6b7280", fontSize: 14 }}>Apply once with your photo. Download it after the admin approves.</div>
                        </div>
                        {!loading && !loadError && <span style={styles.badge(statusInfo.color)}>{statusInfo.label}</span>}
                    </div>

                    {loading && <p className="mt-3 mb-0">Loading...</p>}

                    {loadError && (
                        <div style={styles.note("#b91c1c")}>
                            {loadError}{" "}
                            <button type="button" style={{ ...styles.ghost, padding: "4px 12px", marginLeft: 8 }} onClick={load}>
                                Try again
                            </button>
                        </div>
                    )}

                    {message && <div style={styles.note(message.type === "success" ? "#15803d" : "#b91c1c")}>{message.text}</div>}

                    {!loading && !loadError && (
                        <div className="row mt-3">
                            {showCard && (
                                <div className="col-lg-7 mb-4">
                                    {status === "expired" && <div style={styles.note("#b45309")}>This card has expired. Submit your details again to renew it.</div>}
                                    <CardCanvas card={card} design={design} />
                                    <div className="text-center mt-2" style={{ fontSize: 13, color: "#6b7280" }}>
                                        ID No. {card.card_no} {card.valid_till ? `| Valid till ${formatDate(card.valid_till)}` : ""}
                                    </div>
                                </div>
                            )}

                            <div className={showCard ? "col-lg-5" : "col-lg-8"}>
                                {status === "pending" && (
                                    <div style={styles.note("#b45309")}>
                                        Your request is waiting for admin approval. Your card will appear here once it is approved.
                                    </div>
                                )}
                                {status === "rejected" && (
                                    <div style={styles.note("#b91c1c")}>
                                        Rejected: {card.reject_reason}. Please correct the details and submit again.
                                    </div>
                                )}
                                {status === "revoked" && (
                                    <div style={styles.note("#111827")}>
                                        Your ID card was cancelled{card.reject_reason ? `: ${card.reject_reason}` : ""}. Please contact the admin.
                                    </div>
                                )}

                                {status !== "revoked" && !showForm && (
                                    <button type="button" style={styles.ghost} onClick={() => setShowForm(true)}>
                                        {status === "expired" ? "Renew my card" : status === "pending" ? "Edit my request" : "Correct my details"}
                                    </button>
                                )}

                                {status !== "revoked" && showForm && (
                                    <form onSubmit={submit} noValidate>
                                        <div className="mb-3">
                                            <label style={styles.label} htmlFor="idc_name">Name on card *</label>
                                            <input id="idc_name" style={styles.input} maxLength={100} value={form.name}
                                                onChange={(e) => setForm({ ...form, name: e.target.value })} />
                                        </div>
                                        <div className="mb-3">
                                            <label style={styles.label} htmlFor="idc_mobile">Mobile *</label>
                                            <input id="idc_mobile" type="tel" style={styles.input} maxLength={16} value={form.mobile}
                                                onChange={(e) => setForm({ ...form, mobile: e.target.value })} />
                                        </div>
                                        <div className="mb-3">
                                            <label style={styles.label} htmlFor="idc_address">Full address *</label>
                                            <textarea id="idc_address" rows={2} style={styles.input} maxLength={250} value={form.address}
                                                placeholder="House / street, area, city, state, PIN"
                                                onChange={(e) => setForm({ ...form, address: e.target.value })} />
                                        </div>
                                        <div className="mb-3">
                                            <label style={styles.label} htmlFor="idc_photo">
                                                Passport size photo {card?.photo ? "(leave empty to keep the current photo)" : "*"}
                                            </label>
                                            <div className="d-flex align-items-center" style={{ gap: 12 }}>
                                                {(photoPreview || card?.photo) && <img src={photoPreview || card.photo} alt="" style={styles.photo} />}
                                                <input id="idc_photo" type="file" accept="image/jpeg,image/png,image/webp" onChange={onPhoto} />
                                            </div>
                                            <small style={{ color: "#6b7280" }}>JPG, PNG or WEBP, up to 3 MB. Face clearly visible, plain background.</small>
                                        </div>
                                        <div className="d-flex flex-wrap" style={{ gap: 10 }}>
                                            <button type="submit" style={{ ...styles.button, opacity: saving ? 0.7 : 1 }} disabled={saving}>
                                                {saving ? "Sending..." : status === "none" ? "Apply for ID Card" : "Submit for approval"}
                                            </button>
                                            {!["none", "rejected"].includes(status) && (
                                                <button type="button" style={styles.ghost} onClick={() => setShowForm(false)} disabled={saving}>
                                                    Cancel
                                                </button>
                                            )}
                                        </div>
                                    </form>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </section>
    );
};

export default IdCardSection;
